import { LitElement, html, nothing } from 'lit'
import { customElement, state, query } from 'lit/decorators.js'
import { live } from 'lit/directives/live.js'
import { consume } from '@lit/context'
import { style } from 'components/shared/product-tile-configurator/product-tile-configurator.style.js'
import { modalStyle } from './tile-configurator-modal.style.js'
import { CanvasStateContext, TileConfiguratorActionsContext, type CanvasStateContextType, type TileConfiguratorActionsContextType } from 'components/shared/product-tile-configurator/context.js'
import { LAYOUT_KINDS, MODE_LABELS } from 'components/shared/product-tile-configurator/editor-state.js'
import { countWidgets } from 'components/shared/product-tile-configurator/copy-widgets.js'

type ActiveTab = 'import' | 'export'

@customElement('csc-tile-configurator-import-export-modal')
export class CSCTileConfiguratorImportExportModal extends LitElement {
  static override styles = [style, modalStyle]

  @consume({ context: TileConfiguratorActionsContext })
  private actions!: TileConfiguratorActionsContextType

  @consume({ context: CanvasStateContext, subscribe: true })
  private sectionsByMode!: CanvasStateContextType

  @state()
  private activeTab: ActiveTab = 'import'

  @state()
  private importError: string | null = null

  @state()
  private pasteTextValue = ''

  @state()
  private exportJson = ''

  @state()
  private configCopied = false

  @state()
  private copyFailed = false

  @query('cse-modal')
  private modal!: any

  async openImport() {
    this.activeTab = 'import'
    this.importError = null
    this.pasteTextValue = ''
    await this.updateComplete
    this.modal?.open()
  }

  async openExport() {
    this.activeTab = 'export'
    this.copyFailed = false
    this.exportJson = JSON.stringify(this.actions.exportCombinedConfig(), null, 2)
    await this.updateComplete
    this.modal?.open()
  }

  private async handleClose() {
    this.importError = null
    this.pasteTextValue = ''
    await this.modal?.close()
  }

  private async applyConfigFromText(text: string) {
    this.importError = null

    try {
      const parsed: unknown = JSON.parse(text)
      this.actions.loadFromCombinedConfig(parsed)

      this.pasteTextValue = ''
      await this.modal?.close()

      this.dispatchEvent(new CustomEvent('config-loaded', {
        detail: { config: parsed },
        bubbles: true,
        composed: true
      }))
    } catch (error) {
      this.importError = error instanceof Error ? error.message : 'Failed to load configuration'
      this.dispatchEvent(new CustomEvent('config-load-error', {
        detail: { error: this.importError },
        bubbles: true,
        composed: true
      }))
    }
  }

  private handlePasteTextChange(event: Event) {
    this.pasteTextValue = (event.currentTarget as HTMLElement).textContent ?? ''
  }

  private async handleApplyPastedText() {
    if (!this.pasteTextValue.trim()) {
      this.importError = 'Please paste JSON configuration'
      return
    }
    await this.applyConfigFromText(this.pasteTextValue)
  }

  private async handleCopyExportJson() {
    try {
      await navigator.clipboard.writeText(this.exportJson)
    } catch {
      this.copyFailed = true
      this.selectExportJson()
      return
    }
    this.copyFailed = false
    this.configCopied = true
    setTimeout(() => {
      this.configCopied = false
    }, 2000)
  }

  private selectExportJson() {
    const pre = this.shadowRoot?.querySelector('.json-display')
    const selection = window.getSelection()
    if (!pre || !selection) return
    const range = document.createRange()
    range.selectNodeContents(pre)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  private renderCopyFailedWarning() {
    if (!this.copyFailed) return nothing
    return html`
      <cse-alert variant="warning" class="export-copy-failed-warning">
        Not copied to clipboard
        <span slot="description">This browser blocked clipboard access. The JSON below is selected, press Ctrl+C (Cmd+C on Mac) to copy it.</span>
      </cse-alert>
    `
  }

  private renderUnconfiguredWarnings() {
    const unconfigured = LAYOUT_KINDS.filter(mode => countWidgets(this.sectionsByMode[mode]) === 0)
    if (!unconfigured.length) return nothing
    return html`
      <div class="export-unconfigured-warnings">
        ${unconfigured.map(mode => html`
          <cse-alert variant="warning" class="export-unconfigured-warning" data-mode=${mode}>
            ${MODE_LABELS[mode]} is not configured
            <span slot="description">The exported JSON has no widgets for the ${mode} view. You can still export it.</span>
          </cse-alert>
        `)}
      </div>
    `
  }

  override render() {
    return html`
      <cse-modal size="large">
        <div slot="header" class="configurator-modal-header modal-header">
          <cse-text class="configurator-modal-title" variant="heading-3">${this.activeTab === 'import' ? 'Import' : 'Export'} JSON Configuration</cse-text>
          <cse-icon-button
            variant="secondary"
            @click=${this.handleClose}
            aria-label="Close">
            <cse-icon icon-name="xmark" icon-style="light"></cse-icon>
          </cse-icon-button>
        </div>

        <div class="modal-body-content">
          ${this.activeTab === 'import' ? html`
            <cse-button
              variant="primary"
              @click=${this.handleApplyPastedText}
              ?disabled=${!this.pasteTextValue}>
              Apply Configuration
            </cse-button>

            ${this.importError ? html`
              <cse-text variant="body-s" class="modal-error-text">
                ${this.importError}
              </cse-text>
            ` : nothing}

            <cse-paper variant="secondary" bordered>
              <div class="paper-content">
                <pre
                  class="json-display json-input"
                  contenteditable="plaintext-only"
                  role="textbox"
                  aria-multiline="true"
                  aria-label="JSON configuration"
                  data-placeholder="Paste your JSON configuration here..."
                  .textContent=${live(this.pasteTextValue)}
                  @input=${this.handlePasteTextChange}></pre>
              </div>
            </cse-paper>
          ` : html`
            ${this.renderUnconfiguredWarnings()}

            <cse-button variant=${this.configCopied ? 'success' : 'secondary'} size="small" @click=${this.handleCopyExportJson}>
              <cse-icon icon-name=${this.configCopied ? 'check' : 'copy'} icon-style="light"></cse-icon>
              ${this.configCopied ? 'Copied!' : 'Copy to clipboard'}
            </cse-button>

            ${this.renderCopyFailedWarning()}

            <cse-paper variant="secondary" bordered>
              <div class="paper-content">
                <pre class="json-display">${this.exportJson}</pre>
              </div>
            </cse-paper>
          `}
        </div>
        <span slot="footer"></span>
      </cse-modal>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-tile-configurator-import-export-modal': CSCTileConfiguratorImportExportModal
  }
}
